import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Game, RockColor, RockMetadata } from '../types/curling';
import { getGame, getGamesForSheet, saveGame } from '../utils/storage';
import { ArrowLeft } from 'lucide-react';

interface RockBehavior {
  rockNumber: number;
  totalShots: number;
  avgCurl: number | null;
  avgTime: number | null;
  overCurlCount: number;
  underCurlCount: number;
  heavyCount: number;
  lightCount: number;
  team: 'team1' | 'team2' | null;
  color: string;
  playerName: string | null;
  isSlow: boolean;
  isStraight: boolean;
  isFast: boolean;
  isCutter: boolean;
}

export function RockMap() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId, club, sheet } = useParams<{ gameId?: string; club?: string; sheet?: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [rockBehaviors, setRockBehaviors] = useState<RockBehavior[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [overallAvgCurl, setOverallAvgCurl] = useState<number>(0);
  const [overallAvgTime, setOverallAvgTime] = useState<number>(0);

  const getRockColorStyles = (rockColor?: string): { bgColor: string; textColor: string } => {
    let bgColor = '#e5e7eb'; // gray-200
    let textColor = '#1f2937'; // gray-800

    if (rockColor) {
      switch (rockColor) {
        case 'yellow':
          bgColor = '#fcd34d'; // yellow-300
          textColor = '#78350f'; // yellow-900
          break;
        case 'red':
          bgColor = '#fca5a5'; // red-300
          textColor = '#7f1d1d'; // red-900
          break;
        case 'blue':
          bgColor = '#93c5fd'; // blue-300
          textColor = '#1e3a8a'; // blue-900
          break;
        case 'green':
          bgColor = '#6ee7b7'; // green-300
          textColor = '#064e3b'; // green-900
          break;
        case 'white':
          bgColor = '#ffffff'; // true white, so it doesn't read as the same gray as "no problems" cards
          textColor = '#111827'; // gray-900
          break;
      }
    }

    return { bgColor, textColor };
  };

  /**
   * A rock's card is bordered red, thicker than the default gray, the moment it's
   * out of tolerance in any of the four ways below. The fill says which: a single
   * problem gets that problem's own color, but a rock tripping two or more at once
   * is just filled red too — naming one of several problems as "the" fill color
   * would be arbitrary, and a rock with multiple problems is worth flagging as more
   * notable than one with only one.
   */
  const getRockCardStyle = (rock: RockBehavior): { backgroundColor: string; border: string } => {
    const problemCount = [rock.isSlow, rock.isFast, rock.isStraight, rock.isCutter]
        .filter(Boolean).length;

    if (problemCount === 0) {
      return { backgroundColor: '#e5e7eb', border: '3px solid #9ca3af' }; // gray — in tolerance
    }

    const border = '5px solid #ef4444';
    if (problemCount >= 2) return { backgroundColor: '#fca5a5', border }; // red — multiple problems
    if (rock.isSlow) return { backgroundColor: '#fde047', border }; // yellow
    if (rock.isFast) return { backgroundColor: '#93c5fd', border }; // blue
    if (rock.isStraight) return { backgroundColor: '#fdba74', border }; // orange
    return { backgroundColor: '#c4b5fd', border }; // violet — isCutter
  };

  const getDefaultPlayerAssignments = (playerCount: number): number[] => {
    // Shot patterns based on player count
    const shotPatterns: Record<number, number[]> = {
      2: [0, 1, 1, 1, 0], // 5 shots for 2 players (positions 6-8 will be -1)
      3: [0, 0, 0, 1, 1, 1, 2, 2], // 8 shots for 3 players
      4: [0, 0, 1, 1, 2, 2, 3, 3], // 8 shots for 4 players
      5: [0, 0, 1, 1, 2, 2, 3, 3], // 8 shots for 4 players (5th is substitute)
    };

    const pattern = shotPatterns[playerCount] || [0, 0, 0, 0, 0, 0, 0, 0];

    // For 2-player teams, positions 6-8 are unassigned (-1)
    if (playerCount === 2) {
      return [...pattern, -1, -1, -1];
    }

    return pattern;
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (gameId) {
        // Single game mode
        const foundGame = await getGame(gameId);
        if (cancelled || !foundGame) return;

        setGame(foundGame);
        calculateRockBehaviors(foundGame);
        // Find the most recent shot timestamp
        const mostRecentTimestamp = findMostRecentShot(foundGame);
        if (mostRecentTimestamp) {
          setLastUpdated(new Date(mostRecentTimestamp));
        }
      } else if (club && sheet) {
        // Sheet mode - the server filters by club and sheet. Physical rocks
        // belong to the sheet, not to whichever team happened to throw them
        // in a given game, so this aggregates by rock color/position across
        // every game rather than reusing the single-game team-based path.
        const filteredGames = await getGamesForSheet(decodeURIComponent(club), sheet);
        if (cancelled || filteredGames.length === 0) return;

        setGames(filteredGames);
        calculateSheetRockBehaviors(filteredGames);
        // Find the most recent shot timestamp across all games
        const mostRecentTimestamp = filteredGames.reduce<number | null>((latest, game) => {
          const gameLatest = findMostRecentShot(game);
          return gameLatest && (!latest || gameLatest > latest) ? gameLatest : latest;
        }, null);
        if (mostRecentTimestamp) {
          setLastUpdated(new Date(mostRecentTimestamp));
        }
      }
    };

    load().catch(error => console.error('Failed to load rock map data:', error));

    return () => {
      cancelled = true;
    };
  }, [gameId, club, sheet]);

  const handleMetadataChange = async (
    rockNumber: number,
    field: keyof RockMetadata,
    checked: boolean
  ) => {
    if (!game || !gameId) return;

    // Only this game changes, so save it alone rather than rewriting the whole list.
    const updatedGame: Game = {
      ...game,
      rockMetadata: {
        ...game.rockMetadata,
        [rockNumber]: {
          ...game.rockMetadata?.[rockNumber],
          [field]: checked,
        },
      },
    };

    // Applied locally first so the checkbox responds without waiting on the round trip.
    setGame(updatedGame);
    calculateRockBehaviors(updatedGame); // Recalculate to update highlighting

    try {
      await saveGame(updatedGame);
    } catch (error) {
      console.error('Failed to save rock metadata:', error);
      // Put the previous state back so the UI does not claim a save that failed.
      setGame(game);
      calculateRockBehaviors(game);
    }
  };

  const getRockMetadata = (rockNumber: number): RockMetadata => {
    return game?.rockMetadata?.[rockNumber] || {};
  };

  const findMostRecentShot = (game: Game): number | null => {
    let mostRecent: number | null = null;
    game.ends.forEach(end => {
      end.shots.forEach(shot => {
        if (shot.timestamp && (!mostRecent || shot.timestamp > mostRecent)) {
          mostRecent = shot.timestamp;
        }
      });
    });
    return mostRecent;
  };

  const calculateRockBehaviors = (game: Game) => {
    // Group shots by rock number (1-8 for each team, 16 total)
    const rockCurlData: Record<number, number[]> = {};
    const rockTimeData: Record<number, number[]> = {};
    const rockOverCurlCount: Record<number, number> = {};
    const rockUnderCurlCount: Record<number, number> = {};
    const rockHeavyCount: Record<number, number> = {};
    const rockLightCount: Record<number, number> = {};
    const rockAllShotsCount: Record<number, number> = {};

    // Initialize data structure for 16 rocks
    for (let i = 1; i <= 16; i++) {
      rockCurlData[i] = [];
      rockTimeData[i] = [];
      rockOverCurlCount[i] = 0;
      rockUnderCurlCount[i] = 0;
      rockHeavyCount[i] = 0;
      rockLightCount[i] = 0;
      rockAllShotsCount[i] = 0;
    }

    // Zone adjustments for normalizing times to zone 7
    const zoneAdjustments: Record<number, number> = {
      4: -0.75,
      5: -0.5,
      6: -0.2,
      7: 0,
      8: 0.2,
      9: 0.5,
      10: 0.75,
    };

    // Collect data from all shots in the game
    game.ends.forEach(end => {
      end.shots.forEach((shot, index) => {
        if (!shot.skipped) {
          // Determine rock number based on alternating pattern:
          // Shot 0 (Team 1, Rock 1) = Rock #1
          // Shot 1 (Team 2, Rock 1) = Rock #9
          // Shot 2 (Team 1, Rock 2) = Rock #2
          // Shot 3 (Team 2, Rock 2) = Rock #10
          // etc.
          const isTeam1 = index % 2 === 0;
          const rockWithinTeam = Math.floor(index / 2) + 1; // 1-8
          const rockNumber = isTeam1 ? rockWithinTeam : rockWithinTeam + 8;

          // Skip if rockNumber is out of bounds (more than 8 rocks per team)
          if (rockNumber < 1 || rockNumber > 16) {
            return;
          }

          rockAllShotsCount[rockNumber]++;

          // Collect curl data for shots that ended in zones 6-8 (4-foot area)
          if (shot.actualPosition >= 6 && shot.actualPosition <= 8) {
            if (rockCurlData[rockNumber] && shot.curl !== undefined) {
              rockCurlData[rockNumber].push(shot.curl);
            }
          }

          // Collect time data for shots that ended in zones 4-10 (house shots)
          // Adjust times to be relative to zone 7
          if (shot.actualPosition >= 4 && shot.actualPosition <= 10 && shot.time !== undefined && shot.time > 0) {
            if (rockTimeData[rockNumber]) {
              const adjustment = zoneAdjustments[shot.actualPosition] || 0;
              const adjustedTime = shot.time + adjustment;
              rockTimeData[rockNumber].push(adjustedTime);
            }
          }
          
          // Count over-curl and under-curl when reason is ice/rock
          if (shot.curlReason === 'ice/rock') {
            if (shot.curlAssessment === 'over') {
              rockOverCurlCount[rockNumber]++;
            } else if (shot.curlAssessment === 'under') {
              rockUnderCurlCount[rockNumber]++;
            }
          }

          // Count heavy and light shots when reason is ice/rock
          if (shot.weightReason === 'ice/rock') {
            if (shot.weight === 'heavy') {
              rockHeavyCount[rockNumber]++;
            } else if (shot.weight === 'light') {
              rockLightCount[rockNumber]++;
            }
          }
        }
      });
    });

    // Calculate sheet averages for comparison
    let totalCurl = 0;
    let totalTime = 0;
    let totalCurlShots = 0;
    let totalTimeShots = 0;

    for (let i = 1; i <= 16; i++) {
      rockCurlData[i].forEach(curl => {
        totalCurl += curl;
        totalCurlShots++;
      });
      rockTimeData[i].forEach(time => {
        totalTime += time;
        totalTimeShots++;
      });
    }

    const sheetAvgCurl = totalCurlShots > 0 ? totalCurl / totalCurlShots : 0;

    // Calculate average time for house shots (zones 4-10) to estimate zone 7
    // Times are already adjusted in rockTimeData, just sum them all
    const sheetAvgTime = totalTimeShots > 0 ? totalTime / totalTimeShots : 0;

    // Set overall averages for display
    setOverallAvgCurl(sheetAvgCurl);
    setOverallAvgTime(sheetAvgTime);

    // Helper function to get color info
    const getColorInfo = (rockNumber: number): { team: 'team1' | 'team2'; colorName: string; bgColor: string; textColor: string } => {
      const isTeam1 = rockNumber <= 8;
      const team = isTeam1 ? 'team1' : 'team2';
      const rockColor = isTeam1 ? game.team1RockColor : game.team2RockColor;
      
      let colorName = 'Unknown';
      let bgColor = '#e5e7eb'; // gray-200
      let textColor = '#1f2937'; // gray-800
      
      if (rockColor) {
        colorName = rockColor.charAt(0).toUpperCase() + rockColor.slice(1);
        
        switch (rockColor) {
          case 'yellow':
            bgColor = '#fef3c7'; // yellow-100
            textColor = '#78350f'; // yellow-900
            break;
          case 'red':
            bgColor = '#fee2e2'; // red-100
            textColor = '#7f1d1d'; // red-900
            break;
          case 'blue':
            bgColor = '#dbeafe'; // blue-100
            textColor = '#1e3a8a'; // blue-900
            break;
          case 'green':
            bgColor = '#d1fae5'; // green-100
            textColor = '#064e3b'; // green-900
            break;
          case 'white':
            bgColor = '#f9fafb'; // gray-50
            textColor = '#111827'; // gray-900
            break;
        }
      }
      
      return { team, colorName, bgColor, textColor };
    };

    // Which player threw a given rock. Curling doesn't require a team to throw its
    // eight rocks in numeric sequence, so this looks the rock number up in the
    // team's throwing order rather than assuming position N threw rock N.
    const getPlayerForRock = (rockNumber: number): string | null => {
      const isTeam1 = rockNumber <= 8;
      const rockWithinTeam = isTeam1 ? rockNumber : rockNumber - 8;
      const players = isTeam1 ? game.team1Players : game.team2Players;
      const rockOrder = (isTeam1 ? game.team1RockOrder : game.team2RockOrder);
      const effectiveRockOrder = rockOrder && rockOrder.length > 0 ? rockOrder : [1, 2, 3, 4, 5, 6, 7, 8];
      const playerAssignments = isTeam1 ? game.team1PlayerAssignments : game.team2PlayerAssignments;
      const effectiveAssignments = playerAssignments && playerAssignments.length > 0
        ? playerAssignments : getDefaultPlayerAssignments(players.length);

      const index = effectiveRockOrder.indexOf(rockWithinTeam);
      if (index === -1) return null;
      const playerIndex = effectiveAssignments[index];
      return playerIndex !== undefined && playerIndex !== -1 ? (players[playerIndex] ?? null) : null;
    };

    // Calculate behaviors for each rock
    const behaviors: RockBehavior[] = [];
    for (let i = 1; i <= 16; i++) {
      const curls = rockCurlData[i];
      const times = rockTimeData[i];
      const colorInfo = getColorInfo(i);
      
      const avgCurl = curls.length > 0 
        ? curls.reduce((sum, c) => sum + c, 0) / curls.length 
        : null;
      
      const avgTime = times.length > 0 
        ? times.reduce((sum, t) => sum + t, 0) / times.length 
        : null;

      const overCurlCount = rockOverCurlCount[i];
      const underCurlCount = rockUnderCurlCount[i];
      const heavyCount = rockHeavyCount[i];
      const lightCount = rockLightCount[i];
      const totalShots = rockAllShotsCount[i];

      // Four independent tolerance checks — a rock can trip more than one at once
      // (e.g. both slow and a cutter), matching the four manual checkboxes below.
      let isSlow = false;
      let isStraight = false;
      let isFast = false;
      let isCutter = false;

      // Manual checkbox metadata first
      const metadata = game.rockMetadata?.[i] || {};
      if (metadata.slow) isSlow = true;
      if (metadata.straight) isStraight = true;
      if (metadata.fast) isFast = true;
      if (metadata.cutter) isCutter = true;

      if (totalShots >= 3) {
        const underCurlPercentage = totalShots > 0 ? underCurlCount / totalShots : 0;
        const overCurlPercentage = totalShots > 0 ? overCurlCount / totalShots : 0;
        const lightPercentage = totalShots > 0 ? lightCount / totalShots : 0;
        const heavyPercentage = totalShots > 0 ? heavyCount / totalShots : 0;

        // Slow: light weight, or times faster than the sheet average (0.4s). A slow
        // (high-friction) rock needs a harder throw to reach the same call, so it
        // crosses the timed zone quicker than normal — a lower time than usual.
        if (lightPercentage >= 0.5) isSlow = true;
        if (avgTime !== null && sheetAvgTime > 0 && sheetAvgTime - avgTime > 0.4) isSlow = true;

        // Straight: under-curls, or curls less than the sheet average (5in = 0.417ft)
        if (underCurlPercentage >= 0.5) isStraight = true;
        if (avgCurl !== null && sheetAvgCurl > 0 && sheetAvgCurl - avgCurl > 0.417) isStraight = true;

        // Fast: heavy weight, or times slower than the sheet average (0.4s). A fast
        // (low-friction) rock needs less effort to reach the same call, so that
        // gentler throw takes longer to cross the timed zone — a higher time than
        // usual, even though the rock itself is "fast."
        if (heavyPercentage >= 0.5) isFast = true;
        if (avgTime !== null && sheetAvgTime > 0 && avgTime - sheetAvgTime > 0.4) isFast = true;

        // Cutter: over-curls, or curls more than the sheet average (5in = 0.417ft)
        if (overCurlPercentage >= 0.5) isCutter = true;
        if (avgCurl !== null && sheetAvgCurl > 0 && avgCurl - sheetAvgCurl > 0.417) isCutter = true;
      }

      behaviors.push({
        rockNumber: i,
        totalShots,
        avgCurl,
        avgTime,
        overCurlCount,
        underCurlCount,
        heavyCount,
        lightCount,
        team: colorInfo.team,
        color: colorInfo.colorName,
        playerName: getPlayerForRock(i),
        isSlow,
        isStraight,
        isFast,
        isCutter
      });
    }

    setRockBehaviors(behaviors);
  };

  /**
   * Sheet-level rock map: aggregates by physical rock (color + throwing
   * position 1-8), not by team, since which team throws which color flips
   * from game to game while the physical rock set stays with the sheet.
   * Manual slow/straight/fast/cutter flags are OR'd in from every game that
   * used that rock, since there is no single game to persist an edit back
   * to at this level — the checkboxes are read-only in this mode.
   */
  const calculateSheetRockBehaviors = (games: Game[]) => {
    const zoneAdjustments: Record<number, number> = {
      4: -0.75,
      5: -0.5,
      6: -0.2,
      7: 0,
      8: 0.2,
      9: 0.5,
      10: 0.75,
    };

    const bucketKey = (color: RockColor, position: number) => `${color}-${position}`;

    const curlData: Record<string, number[]> = {};
    const timeData: Record<string, number[]> = {};
    const overCurlCount: Record<string, number> = {};
    const underCurlCount: Record<string, number> = {};
    const heavyCount: Record<string, number> = {};
    const lightCount: Record<string, number> = {};
    const allShotsCount: Record<string, number> = {};
    const manualFlags: Record<string, { slow: boolean; straight: boolean; fast: boolean; cutter: boolean }> = {};
    const colorsSeen: RockColor[] = [];

    const ensureColor = (color: RockColor) => {
      if (!colorsSeen.includes(color)) colorsSeen.push(color);
      for (let position = 1; position <= 8; position++) {
        const key = bucketKey(color, position);
        if (!(key in allShotsCount)) {
          curlData[key] = [];
          timeData[key] = [];
          overCurlCount[key] = 0;
          underCurlCount[key] = 0;
          heavyCount[key] = 0;
          lightCount[key] = 0;
          allShotsCount[key] = 0;
          manualFlags[key] = { slow: false, straight: false, fast: false, cutter: false };
        }
      }
    };

    games.forEach(game => {
      const team1Color = game.team1RockColor;
      const team2Color = game.team2RockColor;
      if (team1Color) ensureColor(team1Color);
      if (team2Color) ensureColor(team2Color);

      game.ends.forEach(end => {
        end.shots.forEach((shot, index) => {
          if (shot.skipped) return;

          const isTeam1 = index % 2 === 0;
          const color = isTeam1 ? team1Color : team2Color;
          if (!color) return; // can't attribute a shot to a physical rock without knowing its color

          const position = Math.floor(index / 2) + 1; // 1-8, throw order within the color's set
          if (position < 1 || position > 8) return;
          const key = bucketKey(color, position);

          allShotsCount[key]++;

          if (shot.actualPosition >= 6 && shot.actualPosition <= 8 && shot.curl !== undefined) {
            curlData[key].push(shot.curl);
          }

          if (shot.actualPosition >= 4 && shot.actualPosition <= 10 && shot.time !== undefined && shot.time > 0) {
            const adjustment = zoneAdjustments[shot.actualPosition] || 0;
            timeData[key].push(shot.time + adjustment);
          }

          if (shot.curlReason === 'ice/rock') {
            if (shot.curlAssessment === 'over') overCurlCount[key]++;
            else if (shot.curlAssessment === 'under') underCurlCount[key]++;
          }

          if (shot.weightReason === 'ice/rock') {
            if (shot.weight === 'heavy') heavyCount[key]++;
            else if (shot.weight === 'light') lightCount[key]++;
          }

          // Manual metadata lives on the game, keyed by that game's own
          // team-relative rock number (1-8 for team1's color, 9-16 for team2's).
          const rockNumberInGame = isTeam1 ? position : position + 8;
          const metadata = game.rockMetadata?.[rockNumberInGame];
          if (metadata) {
            const flags = manualFlags[key];
            if (metadata.slow) flags.slow = true;
            if (metadata.straight) flags.straight = true;
            if (metadata.fast) flags.fast = true;
            if (metadata.cutter) flags.cutter = true;
          }
        });
      });
    });

    let totalCurl = 0;
    let totalCurlShots = 0;
    let totalTime = 0;
    let totalTimeShots = 0;
    Object.values(curlData).forEach(list => list.forEach(c => { totalCurl += c; totalCurlShots++; }));
    Object.values(timeData).forEach(list => list.forEach(t => { totalTime += t; totalTimeShots++; }));

    const sheetAvgCurl = totalCurlShots > 0 ? totalCurl / totalCurlShots : 0;
    const sheetAvgTime = totalTimeShots > 0 ? totalTime / totalTimeShots : 0;
    setOverallAvgCurl(sheetAvgCurl);
    setOverallAvgTime(sheetAvgTime);

    const behaviors: RockBehavior[] = [];
    colorsSeen.forEach(color => {
      for (let position = 1; position <= 8; position++) {
        const key = bucketKey(color, position);
        const curls = curlData[key] ?? [];
        const times = timeData[key] ?? [];
        const totalShots = allShotsCount[key] ?? 0;

        const avgCurl = curls.length > 0 ? curls.reduce((sum, c) => sum + c, 0) / curls.length : null;
        const avgTime = times.length > 0 ? times.reduce((sum, t) => sum + t, 0) / times.length : null;
        const over = overCurlCount[key] ?? 0;
        const under = underCurlCount[key] ?? 0;
        const heavy = heavyCount[key] ?? 0;
        const light = lightCount[key] ?? 0;

        const flags = manualFlags[key] ?? { slow: false, straight: false, fast: false, cutter: false };
        let isSlow = flags.slow;
        let isStraight = flags.straight;
        let isFast = flags.fast;
        let isCutter = flags.cutter;

        if (totalShots >= 3) {
          const underCurlPercentage = under / totalShots;
          const overCurlPercentage = over / totalShots;
          const lightPercentage = light / totalShots;
          const heavyPercentage = heavy / totalShots;

          if (lightPercentage >= 0.5) isSlow = true;
          if (avgTime !== null && sheetAvgTime > 0 && sheetAvgTime - avgTime > 0.4) isSlow = true;

          if (underCurlPercentage >= 0.5) isStraight = true;
          if (avgCurl !== null && sheetAvgCurl > 0 && sheetAvgCurl - avgCurl > 0.417) isStraight = true;

          if (heavyPercentage >= 0.5) isFast = true;
          if (avgTime !== null && sheetAvgTime > 0 && avgTime - sheetAvgTime > 0.4) isFast = true;

          if (overCurlPercentage >= 0.5) isCutter = true;
          if (avgCurl !== null && sheetAvgCurl > 0 && avgCurl - sheetAvgCurl > 0.417) isCutter = true;
        }

        behaviors.push({
          rockNumber: position,
          totalShots,
          avgCurl,
          avgTime,
          overCurlCount: over,
          underCurlCount: under,
          heavyCount: heavy,
          lightCount: light,
          team: null,
          color: color.charAt(0).toUpperCase() + color.slice(1),
          playerName: null,
          isSlow,
          isStraight,
          isFast,
          isCutter,
        });
      }
    });

    setRockBehaviors(behaviors);
  };

  const isSheetMode = !gameId && !!(club && sheet);

  if (gameId ? !game : games.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-blue-300">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
          {gameId && (
            <button
              onClick={() => {
                // Return to whichever page the user actually came from (history, the
                // game summary, a club/sheet game list, ...) rather than guessing.
                // Only synthesize a destination if there's no in-app page to pop back to.
                if (location.key !== 'default') {
                  navigate(-1);
                } else {
                  navigate(`/CurlingCodex/game/${gameId}`);
                }
              }}
              className="flex items-center gap-2 text-blue-300 hover:text-blue-200 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          )}
          {club && sheet && (
            <button
              onClick={() => navigate(`/CurlingCodex/club-sheet/${encodeURIComponent(club)}/${sheet}`)}
              className="flex items-center gap-2 text-blue-300 hover:text-blue-200 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Game List
            </button>
          )}

          <h1 className="text-2xl font-bold text-white mb-2">Rock Map</h1>
          {(() => {
            const headerClub = gameId ? game?.curlingClub : (club ? decodeURIComponent(club) : games[0]?.curlingClub);
            const headerSheet = gameId ? game?.sheetNumber : (sheet ?? games[0]?.sheetNumber);
            return (
              <div className="flex gap-3 text-sm text-blue-300">
                {headerClub && <span>{headerClub}</span>}
                {headerClub && headerSheet && <span>•</span>}
                {headerSheet && <span>Sheet {headerSheet}</span>}
                {headerSheet && games.length > 0 && <span>•</span>}
                {games.length > 0 && <span>{games.length} {games.length === 1 ? 'game' : 'games'}</span>}
                {((headerSheet && lastUpdated) || (games.length > 0 && lastUpdated)) && <span>•</span>}
                {lastUpdated && (
                  <span>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                )}
              </div>
            );
          })()}
        </div>

        {/* Overall Averages */}
        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
          <h2 className="text-lg font-bold text-white mb-4">Overall Averages</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-blue-300 mb-1">Average Curl</p>
              <p className="text-3xl font-bold text-white">
                {overallAvgCurl > 0 ? `${overallAvgCurl.toFixed(1)} ft` : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-blue-300 mb-1">Average Time (Zone 7)</p>
              <p className="text-3xl font-bold text-white">
                {overallAvgTime > 0 ? `${overallAvgTime.toFixed(2)} sec` : 'N/A'}
              </p>
              <p className="text-xs text-slate-400 mt-1">Based on house shots (zones 4-10)</p>
            </div>
          </div>
        </div>

        {/* Rock Behaviors Grid. In game mode, the 8 rocks each team throws are
            grouped by team so it's clear which team threw which color and which
            player threw which rock — curling doesn't require a team to throw its
            eight rocks in numeric sequence, so rocks are grouped by team rather
            than by throwing order. In sheet mode there's no single team to group
            by (it flips game to game), so rocks are grouped by physical rock
            color instead — the identity that actually stays with the sheet. */}
        {rockBehaviors.length > 0 ? (
          <div className="space-y-8">
            {(isSheetMode
              ? Array.from(new Set(rockBehaviors.map(r => r.color))).map(colorLabel => ({
                  key: colorLabel,
                  name: null as string | null,
                  rockColor: colorLabel.toLowerCase() as RockColor,
                  rocks: rockBehaviors.filter(r => r.color === colorLabel),
                }))
              : [
                  { key: 'team1', name: game!.team1Name || 'Team 1', rockColor: game!.team1RockColor,
                    rocks: rockBehaviors.filter(r => r.team === 'team1') },
                  { key: 'team2', name: game!.team2Name || 'Team 2', rockColor: game!.team2RockColor,
                    rocks: rockBehaviors.filter(r => r.team === 'team2') },
                ]
            ).map((teamGroup) => {
              const groupColors = getRockColorStyles(teamGroup.rockColor);
              const colorLabel = teamGroup.rockColor
                ? teamGroup.rockColor.charAt(0).toUpperCase() + teamGroup.rockColor.slice(1)
                : 'Unassigned';

              return (
                <div
                  key={teamGroup.key}
                  className="rounded-2xl shadow-xl shadow-black/20 p-6"
                  style={{ backgroundColor: groupColors.bgColor, border: `3px solid ${groupColors.textColor}` }}
                >
                  <h2 className="text-lg font-bold mb-4" style={{ color: groupColors.textColor }}>
                    {teamGroup.name ? (
                      <>{teamGroup.name} <span className="font-normal opacity-70">— {colorLabel} rocks</span></>
                    ) : (
                      <>{colorLabel} rocks</>
                    )}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {teamGroup.rocks.map((rock) => (
              <div
                key={rock.rockNumber}
                className="rounded-lg shadow p-5"
                style={getRockCardStyle(rock)}
              >
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <h3
                      className="text-lg font-bold text-black"
                    >
                      {((rock.rockNumber - 1) % 8) + 1} - {rock.color}
                    </h3>
                    <span
                      className="text-sm text-black opacity-70"
                    >
                      {rock.totalShots} shots
                    </span>
                  </div>
                  {gameId && (
                    <p className="text-xs text-black opacity-60">{rock.playerName || 'Unassigned'}</p>
                  )}
                </div>

                <div className="space-y-3">
                  {/* Average Curl and Time - Same Line */}
                  <div className="flex gap-3">
                    {/* Average Curl */}
                    <div className="flex-1">
                      <p 
                        className="text-xs mb-1 text-black opacity-70"
                      >
                        Average Curl
                      </p>
                      {rock.avgCurl != null ? (
                        <p 
                          className="text-2xl font-bold text-black"
                        >
                          {rock.avgCurl.toFixed(1)} ft
                        </p>
                      ) : (
                        <p 
                          className="text-2xl font-bold text-black opacity-50"
                        >
                          N/A
                        </p>
                      )}
                    </div>

                    {/* Average Time */}
                    <div className="flex-1">
                      <p 
                        className="text-xs mb-1 text-black opacity-70"
                      >
                        Average Time
                      </p>
                      {rock.avgTime != null ? (
                        <p 
                          className="text-2xl font-bold text-black"
                        >
                          {rock.avgTime.toFixed(2)} sec
                        </p>
                      ) : (
                        <p 
                          className="text-2xl font-bold text-black opacity-50"
                        >
                          N/A
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Ice/Rock Issue Counts - All on Same Line */}
                  {(rock.overCurlCount > 0 || rock.underCurlCount > 0 || rock.heavyCount > 0 || rock.lightCount > 0) && (
                    <div className="flex gap-2 pt-2 border-t border-black border-opacity-20">
                      {rock.underCurlCount > 0 && (
                        <div className="flex-1">
                          <p 
                            className="text-xs mb-1 text-black opacity-70"
                          >
                            Under-Curl
                          </p>
                          <p 
                            className="text-lg font-bold text-black"
                          >
                            {rock.underCurlCount}
                          </p>
                        </div>
                      )}
                      {rock.overCurlCount > 0 && (
                        <div className="flex-1">
                          <p 
                            className="text-xs mb-1 text-black opacity-70"
                          >
                            Over-Curl
                          </p>
                          <p 
                            className="text-lg font-bold text-black"
                          >
                            {rock.overCurlCount}
                          </p>
                        </div>
                      )}
                      {rock.lightCount > 0 && (
                        <div className="flex-1">
                          <p 
                            className="text-xs mb-1 text-black opacity-70"
                          >
                            Light
                          </p>
                          <p 
                            className="text-lg font-bold text-black"
                          >
                            {rock.lightCount}
                          </p>
                        </div>
                      )}
                      {rock.heavyCount > 0 && (
                        <div className="flex-1">
                          <p 
                            className="text-xs mb-1 text-black opacity-70"
                          >
                            Heavy
                          </p>
                          <p 
                            className="text-lg font-bold text-black"
                          >
                            {rock.heavyCount}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Rock Characteristics Checkboxes. Editable per-game; in sheet
                      mode there's no single game to save an edit back to, so these
                      just reflect what's already been flagged (manually, in any of
                      the games that used this rock, or automatically) as read-only. */}
                  <div className="pt-3 border-t border-black border-opacity-20">
                    <div className="grid grid-cols-2 gap-2">
                      <label className={`flex items-center gap-2 ${gameId ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
                        <input
                          type="checkbox"
                          checked={gameId ? (getRockMetadata(rock.rockNumber).slow || false) : rock.isSlow}
                          disabled={!gameId}
                          onChange={(e) => handleMetadataChange(rock.rockNumber, 'slow', e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span
                          className="text-sm text-black"
                        >
                          Slow
                        </span>
                      </label>
                      <label className={`flex items-center gap-2 ${gameId ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
                        <input
                          type="checkbox"
                          checked={gameId ? (getRockMetadata(rock.rockNumber).fast || false) : rock.isFast}
                          disabled={!gameId}
                          onChange={(e) => handleMetadataChange(rock.rockNumber, 'fast', e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span
                          className="text-sm text-black"
                        >
                          Fast
                        </span>
                      </label>
                      <label className={`flex items-center gap-2 ${gameId ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
                        <input
                          type="checkbox"
                          checked={gameId ? (getRockMetadata(rock.rockNumber).straight || false) : rock.isStraight}
                          disabled={!gameId}
                          onChange={(e) => handleMetadataChange(rock.rockNumber, 'straight', e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span
                          className="text-sm text-black"
                        >
                          Straight
                        </span>
                      </label>
                      <label className={`flex items-center gap-2 ${gameId ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
                        <input
                          type="checkbox"
                          checked={gameId ? (getRockMetadata(rock.rockNumber).cutter || false) : rock.isCutter}
                          disabled={!gameId}
                          onChange={(e) => handleMetadataChange(rock.rockNumber, 'cutter', e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span
                          className="text-sm text-black"
                        >
                          Cutter
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <p className="text-blue-300 text-lg">No shot data available yet</p>
            <p className="text-slate-400 text-sm mt-2">Start recording shots to see rock behaviors</p>
          </div>
        )}

        {/* Legend: the group each rock sits in is colored and bordered by the team's
            actual rock color; the rock's own card color is unrelated — its border
            just says whether it's in tolerance, and its fill says which of the four
            ways it isn't (or that it's more than one, if so). */}
        {rockBehaviors.length > 0 && (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
            <h2 className="text-lg font-bold text-white mb-4">Legend</h2>
            <p className="text-sm text-blue-200 mb-3">
              A thick red border means the rock is out of tolerance; the fill inside says how.
              A thin gray border means it's fine.
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <span
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: '#e5e7eb', border: '3px solid #9ca3af' }}
                />
                <span className="text-sm text-blue-200">
                  Gray — in tolerance
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: '#fde047', border: '5px solid #ef4444' }}
                />
                <span className="text-sm text-blue-200">
                  Yellow — slow
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: '#fdba74', border: '5px solid #ef4444' }}
                />
                <span className="text-sm text-blue-200">
                  Orange — straight
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: '#93c5fd', border: '5px solid #ef4444' }}
                />
                <span className="text-sm text-blue-200">
                  Blue — fast
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: '#c4b5fd', border: '5px solid #ef4444' }}
                />
                <span className="text-sm text-blue-200">
                  Violet — cutter
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: '#fca5a5', border: '5px solid #ef4444' }}
                />
                <span className="text-sm text-blue-200">
                  Red — two or more problems
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              A rock with just one problem is filled that problem's color; with two or more, it's
              filled red instead of picking one arbitrarily. Each group's bordered background
              above is the rock's own color, not a behavior signal
              {gameId ? " — and the player under each rock's number is who threw it." : '.'}
            </p>
          </div>
        )}

        {/* Info */}
        {rockBehaviors.length > 0 && (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
            <h3 className="text-lg font-semibold text-white mb-4">About Rock Curl</h3>
            <div className="text-sm text-blue-300">
              <p className="mb-2">
                Each rock has unique characteristics that affect how much it curls on the ice. This map shows the
                average curl for each of the 16 rocks {isSheetMode
                  ? `used across ${games.length} recorded ${games.length === 1 ? 'game' : 'games'} on this sheet`
                  : 'used during the game'}.
              </p>
              <p>
                Rocks with higher curl values tend to curve more, while rocks with lower curl values travel straighter. Understanding each rock's behavior helps with rock selection for specific shots.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}